
import { useState, useEffect, useRef } from 'react';
import zxcvbn, { Feedback } from 'zxcvbn-ts';
import { Auth, IdTokenResult, User } from 'firebase/auth';
import { GlyphButton } from '../Controls/Button/GlyphButton';
import { ReactComponent as SaveImage } from '../../public/images/svg/ImageSave.svg';
import { ReactComponent as ShieldCross } from '../../public/images/svg/Shield-Cross.svg';
import { ReactComponent as ShieldTick } from '../../public/images/svg/Shield-Tick.svg';
import { ReactComponent as SignInLogo } from '../assets/icons/Login-01.svg';
import { updatePassword } from "firebase/auth";
import { ModalButton } from '../Controls/Button/ModalButton';
import { ReactComponent as ImageInformation } from '../../public/images/svg/ImageInformation.svg';
import { AccountSignIn } from './AccountSignIn';
import { Overlay, Tooltip } from 'react-bootstrap';
import './css/AccountChangePassword.css';

interface AccountChangePasswordProps {
    auth: Auth;
    onSuccess: (hasChanged:boolean) => void;
}

export const AccountChangePassword: React.FC<AccountChangePasswordProps> = ({ auth, onSuccess }) => {

    const newPasswordRef = useRef<string | null>(null);

    const [isValid, setIsValid] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [closeSignInSignal, setCloseSignInSignal] = useState(0);
    const [hasRecentSignin, setHasRecentSignIn] = useState(false);
    const [warning, setWarning] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<readonly string[] | null>(null);
    const passwordInputRef = useRef<HTMLInputElement | null>(null);

    const showPasswordWarning = warning !== null || suggestions !== null;

    useEffect(() => {
        let cancelled = false;

        async function checkRecency() {
            if (!auth.currentUser) return;

            const token = await auth.currentUser.getIdTokenResult();
            const authTime = new Date(token.authTime).getTime();
            const now = Date.now();
            const minutesSinceAuth = (now - authTime) / 60000;

            if (!cancelled) {
                setHasRecentSignIn(minutesSinceAuth < .1);
            }
        }

        checkRecency();

        return () => {
            cancelled = true;
        };
    }, [closeSignInSignal]);

    return (
        <div className="account-change-password-outer-container">
            {
                hasRecentSignin
                    ? <div className="account-change-password-container">
                        <div className="account-change-password-inner-container">
                            <div className="new-password-fields">
                                <div className="new-password-container">
                                    <div className="password-text-container">
                                        <span>New Password</span>
                                        <input type="password" ref={passwordInputRef} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const result = zxcvbn(e.target.value);
                                            setIsValid(result.score >= 2);
                                            setWarning(result.feedback.warning);
                                            setSuggestions(result.feedback.suggestions);

                                            newPasswordRef.current = e.target.value;
                                        }} />
                                        <div className={`validity-container ${isValid ? 'valid' : 'invalid'}`}>
                                            <ShieldCross className="password-isinvalid" viewBox="0 0 27.9 32" width={27.9} height={32} />
                                            <ShieldTick className="password-isvalid" viewBox="0 0 27.9 32" width={27.9} height={32} />
                                        </div>
                                    </div>

                                </div>
                                <div className="confirm-password-container">
                                    <div className="password-text-container">
                                        <span>Confirm Password</span>
                                        <input type="password" placeholder="********" onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const isIdentical = e.target.value === newPasswordRef.current;
                                            setIsConfirmed(isValid && isIdentical);
                                        }} />

                                        <div className={`validity-container ${isConfirmed ? 'valid' : 'invalid'}`}>
                                            <ShieldCross className="password-isinvalid" viewBox="0 0 27.9 32" width={27.9} height={32} />
                                            <ShieldTick className="password-isvalid" viewBox="0 0 27.9 32" width={27.9} height={32} />
                                        </div>
                                    </div>

                                </div>

                            </div>
                            <Overlay target={passwordInputRef.current} show={showPasswordWarning}>
                                <Tooltip className="password-warning-tooltip">
                                    <div className="confirm-password-stats">
                                        <div>
                                            <span>Warning</span>
                                            <span>{warning}</span>
                                        </div>
                                        <div>
                                            <span>Suggestions</span>
                                            <div>
                                                {suggestions && suggestions.map((suggestion) =>
                                                    <span>{suggestion}</span>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </Tooltip>
                            </Overlay>
                        </div>
                        <GlyphButton ariaLabel="save-password"
                            disabled={!isConfirmed}
                            IconComponent={<SaveImage width={24} height={24} viewBox="0 0 32 32" />} onClick={() => {
                                try {
                                    if (auth.currentUser && newPasswordRef.current) {
                                        updatePassword(auth.currentUser, newPasswordRef.current);
                                        onSuccess(true);
                                    }
                                }
                                catch {
                                    onSuccess(false);
                                }
                            }} />
                    </div>
                    : <div className="security-login-container">
                        <span>For security reasons, you’ll need to sign in again before changing your password. Sensitive actions (like changing your password) require a fresh sign‑in to confirm it’s really you.</span>
                        <ModalButton  modalTitle="Sign In"
                            className="component-button"                 
                            externalCloseSignal={closeSignInSignal}
                            Title="Sign In"
                            IconComponent={<SignInLogo width={32} height={32} viewBox="0 0 32 32" />}>
                            <AccountSignIn onSuccess={() => {
                                setCloseSignInSignal(Date.now());
                            }} />
                        </ModalButton>
                    </div>
            }

        </div>
    );
};
